import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { PermissionName } from '@barahpay/shared';
import { useAuth } from './AuthContext';
import { NotPermitted } from '../components/Feedback';
import { BarahLoader } from '../components/BarahLoader';

/** Blocks the whole shell until a session is known, then requires a signed-in user. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, restoring } = useAuth();
  const location = useLocation();

  if (restoring) {
    return <BarahLoader fullScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  /*
   * A session that still owes its first-boot password change can reach nothing but the profile: the
   * API's PasswordChangeGate refuses every other endpoint (spec section 138).
   *
   * Without this the shell happily routed to the dashboard, whose queries all came back 403, so the
   * first thing a new operator ever saw was a screen of red alerts — with the reason in the server's
   * English detail line and no hint that the fix is on another page. Sending them where the change
   * form actually is makes the UI agree with the gate instead of arguing with it.
   */
  if (user.mustChangePassword && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace />;
  }

  return <>{children}</>;
}

/**
 * Route-level permission gate. This is UX only — every request is authorised again on the server
 * (spec section 65).
 */
export function RequirePermission({
  permissions,
  children,
}: {
  permissions: PermissionName[];
  children: ReactNode;
}) {
  const { canAny } = useAuth();

  return canAny(permissions) ? <>{children}</> : <NotPermitted />;
}
